import { useEffect, useState } from 'react';
import type { SelectorField } from '../../../fields';
import type { CustomerMarketAccount, Security } from '../../../types';
import { isErrorResponse, type RestResponse } from '../../../types/rest';
import { GET, request } from '../../../utils/http';
import { logger } from '../../../utils/logger';
import { isSpot } from '../../../utils/security';

export interface OrderLeverageResponse {
  Account: string;
  Symbol: string;
  Leverage: string;
}

export type UseOrderLeverageArgs = {
  marketAccountField: SelectorField<CustomerMarketAccount>;
  symbolField: SelectorField<Security>;
};

export function useOrderLeverage({
  marketAccountField,
  symbolField,
}: UseOrderLeverageArgs): OrderLeverageResponse | undefined {
  const account = marketAccountField.value?.SourceAccountID;
  const symbol = symbolField.value?.Symbol;
  const security = symbolField.value;

  const [orderLeverage, setOrderLeverage] = useState<OrderLeverageResponse | undefined>();

  useEffect(() => {
    if (!account || !symbol) {
      setOrderLeverage(undefined);
      return;
    }

    // Margin / leverage applies to non-spot instruments only; skip the fetch for spot pairs.
    if (isSpot(security)) {
      setOrderLeverage(undefined);
      return;
    }

    // Leverage only applies when the account has margin trading enabled.
    if (marketAccountField.value?.MarginTrading !== true) {
      setOrderLeverage(undefined);
      return;
    }

    let cancelled = false;
    setOrderLeverage(undefined);

    request<RestResponse<OrderLeverageResponse>>(
      GET,
      `/v1/order-leverage/account/${encodeURIComponent(account)}/symbol/${encodeURIComponent(symbol)}`,
      null
    )
      .then(response => {
        if (cancelled) {
          return;
        }
        if (isErrorResponse(response)) {
          setOrderLeverage(undefined);
          return;
        }
        const orderLeverageResponse = response.data[0];
        if (!orderLeverageResponse?.Leverage) {
          setOrderLeverage(undefined);
          return;
        }
        setOrderLeverage(orderLeverageResponse);
      })
      .catch((err: unknown) => {
        if (cancelled) {
          return;
        }
        logger.warn('Failed to fetch order leverage', { account, symbol, err });
        setOrderLeverage(undefined);
      });

    return () => {
      cancelled = true;
    };
  }, [account, symbol, security, marketAccountField.value?.MarginTrading]);

  return orderLeverage;
}
